// Hashtag utility functions for Znoospace

/**
 * Extract hashtags from text content
 * @param {string} content - The text content to extract hashtags from
 * @returns {string[]} - Array of hashtags (without # symbol)
 */
export const extractHashtags = (content) => {
  if (!content || typeof content !== 'string') return [];
  
  // Regex to match hashtags: # followed by alphanumeric characters, underscores, or hyphens
  const hashtagRegex = /#([a-zA-Z0-9_-]+)/g;
  const matches = content.match(hashtagRegex);
  
  if (!matches) return [];
  
  // Remove # symbol and convert to lowercase for consistency
  return matches.map(tag => tag.substring(1).toLowerCase());
};

/**
 * Extract mentions from text content
 * @param {string} content - The text content to extract mentions from
 * @returns {string[]} - Array of mentions (without @ symbol)
 */
export const extractMentions = (content) => {
  if (!content || typeof content !== 'string') return [];
  
  // Regex to match mentions: @ followed by alphanumeric characters or underscores
  const mentionRegex = /@([a-zA-Z0-9_]+)/g;
  const matches = content.match(mentionRegex);
  
  if (!matches) return [];
  
  // Remove @ symbol and convert to lowercase
  return matches.map(mention => mention.substring(1).toLowerCase());
};

/**
 * SECURITY FIX: Parse hashtags, mentions, and URLs safely for React components
 * @param {string} content - The text content to parse
 * @returns {Array} - Array of text and component objects for safe rendering
 */
export const parseTagsAndMentions = (content) => {
  if (!content || typeof content !== 'string') return [{ type: 'text', content: content || '' }];
  
  const parts = [];
  let lastIndex = 0;
  
  // Enhanced regex for hashtags, mentions, and URLs
  const combinedRegex = /(#[a-zA-Z0-9_-]+|@[a-zA-Z0-9_]+|https?:\/\/[^\s]+)/g;
  let match;
  
  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }
    
    // Add the hashtag, mention, or URL
    const fullMatch = match[0];
    if (fullMatch.startsWith('#')) {
      parts.push({
        type: 'hashtag',
        content: fullMatch,
        tag: fullMatch.slice(1)
      });
    } else if (fullMatch.startsWith('@')) {
      parts.push({
        type: 'mention',
        content: fullMatch,
        mention: fullMatch.slice(1)
      });
    } else if (fullMatch.startsWith('http')) {
      // Handle URLs (including Solana Explorer links)
      let displayText = fullMatch;
      let linkType = 'external';
      
      // Special handling for Solana Explorer links - don't display in content
      if (fullMatch.includes('explorer.solana.com')) {
        // Skip Solana Explorer links entirely in content parsing
        lastIndex = match.index + match[0].length;
        continue;
      }
      
      parts.push({
        type: 'url',
        content: displayText,
        url: fullMatch,
        linkType: linkType
      });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }
  
  return parts;
};

/**
 * DEPRECATED: This function creates XSS vulnerabilities - use parseTagsAndMentions instead
 * @deprecated Use parseTagsAndMentions for safe React rendering
 */
export const highlightTagsAndMentions = (content) => {
  console.warn('⚠️ SECURITY WARNING: highlightTagsAndMentions is deprecated due to XSS vulnerability. Use parseTagsAndMentions instead.');
  return content; // Return plain text to prevent XSS
};

/**
 * Search posts by hashtag, keyword, or author
 * @param {Array} posts - Array of posts to search through
 * @param {string} query - Search query
 * @param {string} type - Search type: 'hashtag', 'keyword', 'author', or 'all'
 * @returns {Array} - Filtered array of posts
 */
export const searchPosts = (posts, query, type = 'all') => {
  if (!posts || !Array.isArray(posts) || !query) return posts;
  
  const searchQuery = query.toLowerCase().trim();
  
  return posts.filter(postAccount => {
    const post = postAccount.account;
    const content = post.content?.toLowerCase() || '';
    const author = post.author?.toString().toLowerCase() || '';
    
    switch (type) {
      case 'hashtag':
        // Search for hashtag (with or without # symbol)
        const hashtagQuery = searchQuery.startsWith('#') ? searchQuery.substring(1) : searchQuery;
        const hashtags = extractHashtags(post.content || '');
        return hashtags.includes(hashtagQuery);
        
      case 'keyword':
        // Search in content only
        return content.includes(searchQuery);
        
      case 'author':
        // Search by author address or username
        return author.includes(searchQuery);
        
      case 'all':
      default:
        // Search in content, hashtags, and author
        const hashtags_all = extractHashtags(post.content || '');
        const hasHashtag = hashtags_all.some(tag => tag.includes(searchQuery.replace('#', '')));
        return content.includes(searchQuery) || author.includes(searchQuery) || hasHashtag;
    }
  });
};

/**
 * Get trending hashtags from posts
 * @param {Array} posts - Array of posts to analyze
 * @param {number} limit - Maximum number of trending tags to return
 * @returns {Array} - Array of objects with tag and count
 */
export const getTrendingHashtags = (posts, limit = 10) => {
  if (!posts || !Array.isArray(posts)) return [];
  
  const hashtagCounts = {};
  
  // Count hashtag occurrences
  posts.forEach(postAccount => {
    const post = postAccount.account;
    const hashtags = extractHashtags(post.content || '');
    
    hashtags.forEach(tag => {
      hashtagCounts[tag] = (hashtagCounts[tag] || 0) + 1;
    });
  });
  
  // Sort by count and return top tags
  return Object.entries(hashtagCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([tag, count]) => ({ tag, count }));
};

/**
 * Format hashtag for display
 * @param {string} tag - Hashtag without # symbol
 * @returns {string} - Formatted hashtag with # symbol
 */
export const formatHashtag = (tag) => {
  return `#${tag}`;
};

/**
 * Validate hashtag format
 * @param {string} tag - Hashtag to validate
 * @returns {boolean} - Whether the hashtag is valid
 */
export const isValidHashtag = (tag) => {
  if (!tag || typeof tag !== 'string') return false;
  
  // Remove # if present
  const cleanTag = tag.startsWith('#') ? tag.substring(1) : tag;
  
  // Check if it matches valid hashtag pattern
  const hashtagPattern = /^[a-zA-Z0-9_-]+$/;
  return hashtagPattern.test(cleanTag) && cleanTag.length > 0 && cleanTag.length <= 50;
};
